import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import {
    allColumns,
    allColumnsWithProvider,
    extendProductColumns,
    REQUIRED_RETURNS_GRID_ALL_PROVIDERS_COLUMNS,
    REQUIRED_RETURNS_GRID_COLUMNS
} from './constants';
import Table from 'src/components/Table';
import {
    ExportTypeType,
    OnChangeOptionsType,
    RowActionPropsType,
    SelectRowType,
    TableColumnType,
    TablePropsOptionsType,
    TableRefType,
    TableRowType
} from 'src/types/table';
import { OrderIcon, ReturnIcon } from 'src/components/SkladonIcon/Icons';
import { useDebounce } from 'src/utils/debounce';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import filterUnique from 'src/utils/filterUnique';
import { ReturnType } from 'src/types/return';
import { getReturnsGridFetch } from 'src/resources/returnsGrid/reducer';
import { orderTypeCodes } from 'src/resources/constantsCodes/constants';
import { useCheckPermission } from 'src/utils/checkPermission';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { patchMultipleReturnsFetch, synchronizeReturnsDCIXFetch } from 'src/resources/returnsUpdate/reducer';
import checkOrChangeProvider from 'src/utils/checkOrChangeProvider';
import {
    RETURN_GRID_ALL_PROVIDERS_URL,
    RETURN_GRID_PRODUCTS_URL,
    RETURN_GRID_URL
} from 'src/resources/returnsGrid/sagas';
import { ConstantsCodesObjectType, ConstantsCodeType } from 'src/types/constantsCodes';
import { disabbleMessages } from 'src/resources/alertMessages/reducer';
import getStatusColor from 'src/utils/getStatusColor';
import colorOptionFce from 'src/utils/colorOptionFce';
import { buildShipperServiceOptions, mapShipperServiceFilters } from 'src/utils/shipperServiceFilter';

export const SESSION_NAME = 'returnsTable';

const customRender = (
    column: TableColumnType,
    row: TableRowType,
    statusCodesObject: ConstantsCodesObjectType
): ReactNode | string => {
    switch (column.key) {
        case 'STATUS':
            return (
                <span style={{ color: getStatusColor(statusCodesObject[row.STATUS]?.color) }}>
                    {statusCodesObject[row.STATUS]?.label}
                </span>
            );
        default:
            return row[column.key];
    }
};

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'ID', direction: 'DESC' },
    page: 1,
    items: 20,
    columns: []
};

const AllReturnsContainer: FC = () => {
    const dispatch = useDispatch();
    const tableRef = useRef<TableRefType>(null);

    const sessionOptions = JSON.parse(sessionStorage.getItem(SESSION_NAME) || 'null');

    const [allProviders, setAllProviders] = useState<boolean>(false);
    const [formatedData, setFormatedData] = useState<ReturnType[]>([]);
    const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(sessionOptions || initTableOptions);
    const [selectedRows, setSelectedRows] = useState<SelectRowType[]>([]);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const loading = useSelector((state: RootStoreType) => state.returnsGrid.loading);
    const loadingUpdate = useSelector((state: RootStoreType) => state.returnsUpdate.loading);
    const loadingUpdateMultiple = useSelector((state: RootStoreType) => state.returnsUpdate.loadingMultiple);
    const returnsData = useSelector((state: RootStoreType) => state.returnsGrid.data);
    const provider = useSelector((state: RootStoreType) => state.provider.data);

    const returnStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.returnStatusCodes);
    const returnStatusCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.returnStatusCodes
    );
    const returnResolutionCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.returnResolutionCodes
    );
    const transportPaidByCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.transportPaidByCodes);
    const sourceSysCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.sourceSysCodes);
    const shipperCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.shipperCodes);
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes);
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);
    const providers = useSelector((state: RootStoreType) => state.user.data?.providers);

    const permissionAllProviders = useCheckPermission('RETURN.LISTALLPROVIDERS');
    const permissionReturnCreate = useCheckPermission('RETURN.CREATE');
    const permissionReturnDetail = useCheckPermission('RETURN.DETAIL');
    const permissionReturnUpdate = useCheckPermission('RETURN.UPDATE');
    const permissionOrderDetail = useCheckPermission('ORDER.DETAIL');
    const permissionSynchronizeDcix = useCheckPermission('RETURN.ACTION.DCIX.IMPORT');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('returns.title') }));
    }, []);

    const debouncedTableOptions = useDebounce(tableOptions, 500);

    useEffect(() => {
        loadTableData();
    }, [debouncedTableOptions]);

    useEffect(() => {
        if (returnsData?.data) {
            setFormatedData(getFormatedData(returnsData?.data || []));
        }
    }, [returnsData?.data]);

    const getFormatedRow = (row: TableRowType): TableRowType => {
        const provider = providers?.find((provider) => provider.ESHOP_KEY === row.ESHOP_KEY);
        return { ...row, providerName: provider?.COMPANY_NAME };
    };

    const getFormatedData = (data: ReturnType[]): ReturnType[] => {
        if (!allProviders) {
            return data;
        }
        return data.map((item) => {
            return getFormatedRow(item);
        });
    };

    const getAllGridParams = () => ({
        filters: [
            mapShipperServiceFilters(
                [...(tableOptions.filters || [])],
                'ORDER_SHIPPER_SERVICE_CODE',
                'ORDER_SHIPPER_SERVICE',
                serviceCodes
            )
        ],
        sorts: [tableOptions.sort],
        page: tableOptions.page,
        items: tableOptions.items,
        columns: [
            ...(allProviders ? REQUIRED_RETURNS_GRID_ALL_PROVIDERS_COLUMNS : REQUIRED_RETURNS_GRID_COLUMNS),
            ...tableOptions.columns.filter((column) => !column.allwaysShow).map((column) => column.key)
        ].filter(filterUnique)
    });

    const loadTableData = () => {
        if (tableOptions.columns.length) {
            dispatch(getReturnsGridFetch({ params: getAllGridParams(), allProviders }));
        }
    };

    const handleChangeOptions: OnChangeOptionsType = (newOptions) => {
        setTableOptions(newOptions);
    };

    const handleExport = (type: ExportTypeType, withProducts?: boolean) => {
        let fileName = 'returns.action.exportFileName';
        if (withProducts) {
            fileName = 'returns.action.exportWithProductsFileName';
        }
        tableRef.current?.exportTableGrid(
            type,
            translate(fileName),
            getAllGridParams,
            allProviders ? RETURN_GRID_ALL_PROVIDERS_URL : withProducts ? RETURN_GRID_PRODUCTS_URL : RETURN_GRID_URL,
            withProducts ? extendProductColumns : undefined,
            { rowFunction: allProviders ? getFormatedRow : undefined }
        );
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const changeProvider = (row: TableRowType) => {
        if (allProviders && providers && provider) {
            if (checkOrChangeProvider(providers, provider, 'ESHOP_KEY', row.ESHOP_KEY)) {
                dispatch(disabbleMessages());
            }
        }
    };

    const reloadTableData = () => {
        dispatch(getReturnsGridFetch(undefined));
    };

    const handleSynchronizeDCIX = () => {
        setConfirmParams({
            title: translate('returns.action.synchronizeDcix'),
            content: translate('returns.action.synchronizeDcixText', { count: selectedRows.length }),
            action: () => {
                dispatch(
                    synchronizeReturnsDCIXFetch(
                        returnsData?.data.filter((item) => selectedRows.includes(String(item.ID)))
                    )
                );
            }
        });
    };

    const handleMassChangeStatus = (statuses: ConstantsCodeType[]) => {
        setConfirmParams({
            title: translate('returns.action.changeStatuses'),
            content: translate('returns.action.changeStatusesText', { count: selectedRows.length }),
            options: statuses,
            optionsValue: statuses[0].value,
            action: (value: ExportTypeType) => {
                dispatch(
                    patchMultipleReturnsFetch({
                        returns: returnsData?.data.filter((returns) => selectedRows.includes(String(returns.ID))),
                        data: { STATUS: value }
                    })
                );
            }
        });
    };

    const tableRowActions: RowActionPropsType[] = [
        {
            icon: <ReturnIcon />,
            label: translate('returns.action.returnDetail'),
            getLink: (row) => `/returns/${row.ID}/`,
            onClick: changeProvider,
            hiddenFce: () => !permissionReturnDetail
        },
        {
            icon: <OrderIcon />,
            label: translate('returns.action.orderDetail'),
            getLink: (row) => `/orders/${row.ORDER_ID}/`,
            onClick: changeProvider,
            hiddenFce: (row) => !row.ORDER_ID || !permissionOrderDetail
        }
    ];

    const shipperServiceCodes = useMemo(
        () => buildShipperServiceOptions(serviceCodes, shipperCodes),
        [serviceCodes, shipperCodes]
    );
    const tableSelectOptions: {
        [key: string]: ConstantsCodeType[];
    } = {
        returnStatusCodes,
        orderTypeCodes,
        returnResolutionCodes,
        transportPaidByCodes,
        sourceSysCodes,
        shipperCodes,
        shipperServiceCodes
    };

    return (
        <>
            <TopBlock
                handleExport={handleExport}
                allProviders={allProviders}
                setAllProviders={setAllProviders}
                reloadTableData={reloadTableData}
                selectedRows={selectedRows}
                permissionReturnCreate={permissionReturnCreate}
                permissionSynchronizeDcix={permissionSynchronizeDcix}
                handleSynchronizeDCIX={handleSynchronizeDCIX}
                permissionAllProviders={permissionAllProviders}
                handleMassChangeStatus={handleMassChangeStatus}
                returnStatusCodes={returnStatusCodes}
                returnStatusCodesObject={returnStatusCodesObject}
                permissionReturnUpdate={permissionReturnUpdate}
                returnsDataData={returnsData?.data || []}
            />
            <Table
                allColumns={allProviders ? allColumnsWithProvider : allColumns}
                ref={tableRef}
                rowIdKey="ID"
                rows={formatedData}
                loading={loading || loadingUpdate || loadingUpdateMultiple}
                columsLabelPrefix="returns.table"
                options={tableOptions}
                totalCount={returnsData?.totalCount}
                onChangeOptions={handleChangeOptions}
                rowActions={tableRowActions}
                selectOptions={tableSelectOptions}
                userSettingsName="showReturnsTableColumns"
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
                optionsSessionName={SESSION_NAME}
                selectedRows={!allProviders ? selectedRows : undefined}
                setSelectedRows={!allProviders ? setSelectedRows : undefined}
                fullHeight
                customRender={(column, row) => customRender(column, row, returnStatusCodesObject)}
                optionStyleFce={colorOptionFce}
            />
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default AllReturnsContainer;
